import React from "react";

import Icon from "@components/Icon";
import { buyFlowTextStyles, colours, device, mediaQuery } from "@helpers";
import { Controller } from "react-hook-form";
import styled from "styled-components";
import FormError from "./FormError";

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  margin-bottom: 1.5rem;
  width: 100%;
`;

const Label = styled.label`
  ${buyFlowTextStyles.SEMISMALL}
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  font-size: 14px;
  
  ${mediaQuery.tabletAndUp} {
    font-size: 15px;
  }
`;

const InputContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  border: 1px solid hsl(0, 0%, 80%);
  border-color: ${(props) => {
		if (props.valid) return colours.VALID;
		if (props.isError) return colours.ERROR;
		return "hsl(0, 0%, 80%)";
	}};
  transition: border-color 100ms ease-out;
  width: 100%;
`;

const DateInput = styled.input`
  ${buyFlowTextStyles.HEADING2}
  caret-color: ${colours.PEACH};
  width: 100%;
  height: 40px;
  padding-left: 1rem;
  padding-right: 45px;
  box-sizing: border-box;
  border: none;
  background: transparent;
  color: ${colours.MIDNIGHT_BLUE};
  
  ${mediaQuery.desktop} {
    ${buyFlowTextStyles.HEADING1_5}
  }
  
  :focus {
    outline: none;
  }
  
  ::-webkit-calendar-picker-indicator {
    opacity: 0;
    position: absolute;
    right: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  z-index: 2;
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  
  /* Ensure touch target is at least 44x44px on mobile for better UX */
  @media (max-width: ${device.tablet}) {
    width: 44px;
    height: 44px;
    right: 0.5rem;
    padding: 10px;
  }

  @media (min-width: ${device.tablet}) {
    width: 20px;
    height: 20px;
  }
  
  img {
    width: 20px;
    height: 20px;
    
    @media (max-width: ${device.tablet}) {
      width: 24px;
      height: 24px;
    }
  }
`;

const requiredFiledMessage = "This is a required field";

const DatepickerInput = ({
	label,
	control,
	required,
	errors,
	name,
	dateMin,
	dateMax,
}) => {
	const getErrorMessage = () => errors?.[name]?.message;

	const currentYear = new Date().getFullYear();

	const minDate =
		dateMin || new Date(new Date().setFullYear(currentYear - 100));

	const maxData =
		dateMax || new Date(new Date().setFullYear(currentYear + 100));

	const errorMessage = getErrorMessage();

	const openDatepicker = () => {
		const input = document.getElementById(name);
		if (input) {
			if (typeof input.showPicker === "function") {
				input.showPicker();
			} else {
				input.focus();
				input.click();
			}
		}
	};

	return (
		<Container>
			{label && (
				<Label htmlFor={name} className={`${required && "required"}`}>
					{label}
				</Label>
			)}
			<InputContainer isError={!!errorMessage}>
				<Controller
					control={control}
					name={name}
					rules={{ required: requiredFiledMessage }}
					render={({ field, value, onChange }) => {
						const fieldValue = field?.value ?? value;
						const handleChange = field?.onChange ?? onChange;

						return (
							<DateInput
								type="date"
								id={name}
								value={
									fieldValue
										? new Date(fieldValue).toISOString().split("T")[0]
										: ""
								}
								onChange={(e) => {
									const date = e.target.value ? new Date(e.target.value) : null;
									if (handleChange) {
										handleChange(date);
									}
								}}
								min={minDate ? minDate.toISOString().split("T")[0] : undefined}
								max={maxData ? maxData.toISOString().split("T")[0] : undefined}
								autoComplete="off"
							/>
						);
					}}
				/>
				<IconWrapper onClick={openDatepicker}>
					<Icon iconName="calendar" alt="calendar icon" />
				</IconWrapper>
			</InputContainer>
			{errorMessage && <FormError>{errorMessage}</FormError>}
		</Container>
	);
};

export default DatepickerInput;
